import React, { forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { process } from '@progress/kendo-data-query';
import useTranslate from '../../../../../../common/hooks/useTranslate';
import FilterCtx from '../../../../../../context/filterContext/filterCtx';
import { useParams } from 'react-router-dom';

import { MultiPeriodGridProps } from './multiPeriodGridProps';

import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import NumericCell from '../../../cells/numericCell/numericCell';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import CashflowCell from '../../cashflow/cells/cashflowCell/cashflowCell';
import StaticPropertyCell from '../cells/staticPropertyCell/staticPropertyCell';
import MobileDataItem from '../cells/mobileDataItem/mobileDataItem';
import { getFieldName } from '../../../../../../common/utility/getFieldName';

const MultiPeriodGrid: React.FC<MultiPeriodGridProps> = forwardRef(
  ({
     gridData,
    filter,
     openCashflowDialog,
     setup,
     widget,
     gridClassName,
     gridColumns,
     selectedCurrency,
     sortDescriptors,
     boards,
     isMobile,
     boardData,
   }, ref: any) => {
    const filterCtx = useContext(FilterCtx);
    const params = useParams();

    const initialDataState = {
      ...filterCtx.gridWidgetFilterState,
      take: undefined,
      sort: [
        {
          field: 'periodName',
          dir: 'desc',
        },
      ],
      filter: filter,
      skip: 0,
    };

    const [dataState, setDataState] = useState<any>(filterCtx.gridWidgetFilterState);
    const [resultState, setResultState] = useState<any>({});
    const [oneNodeIndicator, setOneNodeIndicator] = useState<boolean>(false);

    const { t } = useTranslate();

    const onDataStateChange = useCallback(
      (e: GridDataStateChangeEvent) => {
        if (e.dataState.sort) e.dataState.sort = sortDescriptors(e.dataState.sort);

        setDataState(e.dataState);
        setResultState(process(gridData, e.dataState));
      },
      [gridData],
    );

    const CashflowCellRender = (column) => {
      return (props) => <CashflowCell {...props} handleCashflowOpen={openCashflowDialog} boardId={params.boardId || boardData.boardId}
                                      scale={column.scale} columnName={column.name} selectedCurrency={selectedCurrency}
                                      singlePeriod={setup?.singlePeriod} investorObjectId={params.investorId || widget.investorObjectId}  />;
    };

    const NonCashflowRender = (column) => {
      return (props) => (column.type === 'property' ? <StaticPropertyCell {...props} /> :
        <NumericCell scale={column.scale} {...props} />);
    };

    useEffect(() => {
      //@ts-ignore
      setResultState(process(gridData, initialDataState));
      setDataState(initialDataState);
    }, [gridData]);

    useEffect(() => {
      let nodes: any = [];
      gridColumns?.forEach((column) => !nodes.includes(column.nodeId) && nodes.push(column.nodeId));
      setOneNodeIndicator(nodes.length === 1);
    }, [gridColumns]);

    useEffect(() => {
      filterCtx.setGridWidgetFilterState(dataState);
    }, [dataState]);

    return (
      <>
        {isMobile && oneNodeIndicator && <ul className='mobile-grid-wrapper'>
          {resultState.data?.map((dataItem, key) => {
            return <MobileDataItem dataItem={dataItem} key={key} boardId={parseInt(params.boardId)}
                                   staticProperties={boardData.staticProperties} columns={gridColumns}
                                   handleOpenCashflow={openCashflowDialog} singlePeriod={setup.singlePeriod}
                                   link={`/boards/${params.investorId}/${widget.linkContentId}/${dataItem.objectId}`}
                                   isLink={widget.linkContentId && boards.find((item) => item.id === widget.linkContentId)} />;
          })}
        </ul>}
        {((isMobile && !oneNodeIndicator) || !isMobile) &&
          <ExcelExport data={resultState.data} ref={ref} fileName={widget.name || t('EXPORT')}>
            <Grid
              data={resultState.data}
              {...dataState}
              resizable={true}
              onDataStateChange={onDataStateChange}
              sortable={true}
              className={`numeric-table-style-wrapper ${gridClassName ? gridClassName : ''}`}
              scrollable={'scrollable'}>
              <GridColumn field='periodName' title={t('WIDGET.PERIOD_TITLE')} width={175} locked />
              {gridColumns.map((column, key) => {
                if (column.isTotal)
                  if (oneNodeIndicator) {
                    return (
                      <GridColumn
                        title={column.name}
                        key={`top_${key}`}
                        field={getFieldName(column, true)}
                        width={column.type === 'property' ? 225 : 205}
                        cell={NonCashflowRender(column)}
                      />
                    );
                  } else {
                    return (
                      <GridColumn title={column.name} key={`top_${key}`}>
                        {
                          (
                            <GridColumn
                              field={getFieldName(column, true)}
                              title={t('TOTAL')}
                              width={column.type === 'property' ? 225 : 205}
                              key={key}
                              cell={NonCashflowRender(column)}
                            />
                          ) as any
                        }
                      </GridColumn>
                    );
                  }
                if (oneNodeIndicator) {
                  return (
                    <GridColumn
                      title={column.name}
                      field={getFieldName(column, true)}
                      width={column.type === 'property' ? 225 : 205}
                      cell={column.viewCashflows ? CashflowCellRender(column) : NonCashflowRender(column)}
                      key={`top_${key}`}
                    />
                  );
                } else {
                  return (
                    <GridColumn title={column.name} key={`top_${key}`}>
                      {
                        (
                          <GridColumn
                            field={getFieldName(column, true)}
                            title={column.nodeName}
                            width={column.type === 'property' ? 225 : 205}
                            key={key}
                            cell={column.viewCashflows ? CashflowCellRender(column) : NonCashflowRender(column)}
                          />
                        ) as any
                      }
                    </GridColumn>
                  );
                }
              })}
            </Grid>
          </ExcelExport>}
      </>
    );
  },
);

export default MultiPeriodGrid;
